// @flow
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl, type IntlShape } from 'react-intl';

import {
  getApiVersion,
  getAssetCapCount,
  getEnabledMediaEnginesCount,
  getExtensisAccount,
  getLicensedFeatures,
  getLicenses,
  getMaxSeatCount,
  getMediaEngineTotalSeatCount,
  getSeatCount,
  getTotalAssetCount,
} from 'state/selectors';
import localizedStrings from 'util/localizedStrings';
import { appVersion, buildVersion } from 'util/constants';
import type { License, LicensedFeatures } from 'state/types';
import {
  daysUntilExpiration,
  isDemoMode,
  SN_TYPE_DEMO,
} from 'util/licenseUtils';

type Props = {
  ApiVersion: string,
  assetCapCount: number,
  enabledMediaEnginesCount: number,
  extensisAccount: ?string,
  intl: IntlShape,
  licensedFeatures: LicensedFeatures,
  licenses: License[],
  maxSeatCount: number,
  mediaEngineTotalSeatCount: number,
  seatCount: number,
  totalAssetCount: number,
};

const ASSET_CAP_DANGER_ZONE: number = 10000;

class Row extends Component<{
  id: string,
  label: string,
  value: string,
  valueClassName?: string,
}> {
  render() {
    const { id, label, value, valueClassName } = this.props;
    return (
      <Fragment>
        <div>{label}</div>
        <div
          className={`status-page-grid-item-right ${valueClassName || ''}`}
          data-test-name={id}
        >
          {value}
        </div>
      </Fragment>
    );
  }
}

class ProductDetails extends Component<Props> {
  getDemoDaysRemaining: Function;

  constructor() {
    super();
    this.getDemoDaysRemaining = this.getDemoDaysRemaining.bind(this);
  }

  getDemoDaysRemaining() {
    const { licenses } = this.props;
    const demoLicense = licenses.find(
      license => license.productId === SN_TYPE_DEMO
    );
    return demoLicense ? daysUntilExpiration(demoLicense) : 0;
  }

  getIconForFeature(feature: string) {
    const { licensedFeatures } = this.props;
    return licensedFeatures[feature] ? (
      <i className="fa fa-check-circle fa-lg has-text-primary licenseIcon" />
    ) : (
      <i className="fa fa-minus-circle fa-lg has-text-danger licenseIcon" />
    );
  }

  render() {
    const {
      ApiVersion,
      assetCapCount,
      enabledMediaEnginesCount,
      extensisAccount,
      intl: { formatMessage, formatNumber },
      licensedFeatures,
      licenses,
      maxSeatCount,
      mediaEngineTotalSeatCount,
      seatCount,
      totalAssetCount,
    } = this.props;

    if (!extensisAccount && (!licenses || !isDemoMode(licenses))) {
      return <div />;
    }

    const isNearAssetCap =
      totalAssetCount + ASSET_CAP_DANGER_ZONE >= assetCapCount;
    const dangerZoneClass = 'has-text-danger has-text-weight-bold';
    const assetCapClass = isNearAssetCap ? dangerZoneClass : '';
    const apiLicensed = licensedFeatures.api
      ? formatMessage(localizedStrings.STATUS_API_STATE_LICENSED)
      : formatMessage(localizedStrings.STATUS_API_STATE_UNLICENSED);
    const apiValue = `${ApiVersion} ${apiLicensed}`;
    const mediaEngineValue = `${enabledMediaEnginesCount} / ${mediaEngineTotalSeatCount}`;
    const assetValue = `${formatNumber(totalAssetCount)} / ${formatNumber(
      assetCapCount
    )}`;
    const seatValue = `${formatNumber(
      seatCount > maxSeatCount ? maxSeatCount : seatCount
    )} / ${formatNumber(maxSeatCount)}`;

    return (
      <div className="box">
        <div className="title">
          Portfolio {appVersion}
          {licenses && isDemoMode(licenses) && (
            <Fragment>
              &nbsp;
              <FormattedMessage {...localizedStrings.PortfolioDemo} />
              &nbsp;
              {formatMessage(localizedStrings.LIC_DAYS_REMAINING, {
                days: this.getDemoDaysRemaining(),
              })}
            </Fragment>
          )}
        </div>
        <div style={{ display: 'flex' }}>
          <div>
            <div className="status-page-grid">
              <Row
                id="version-number"
                label={formatMessage(localizedStrings.ABOUT_VERSION)}
                value={buildVersion}
              />
              <Row
                id="api-version-info"
                label={formatMessage(localizedStrings.STATUS_API_VERSION_LABEL)}
                value={apiValue}
              />
              <Row
                id="media-engine-seat-count"
                label={formatMessage(
                  localizedStrings.STATUS_MEDIA_ENGINES_LABEL
                )}
                value={mediaEngineValue}
              />
              <Row
                id="asset-count"
                label={formatMessage(localizedStrings.TOTAL_ASSETS)}
                value={assetValue}
                valueClassName={assetCapClass}
              />
              <Row
                id="seat-count"
                label={formatMessage(localizedStrings.STATUS_CONNECTED_USERS)}
                value={seatValue}
              />
            </div>
          </div>

          <div className="status-page-grid-vrule">&nbsp;</div>

          <div className="">
            <div className="status-page-feature">
              {this.getIconForFeature('np')}&nbsp;NetPublish
            </div>
            <div className="status-page-feature">
              {this.getIconForFeature('me')}&nbsp;Media Engine
            </div>
            <div className="status-page-feature">
              {this.getIconForFeature('sso')}
              &nbsp;
              <FormattedMessage {...localizedStrings.CALL_ATT_SSO_TITLE} />
            </div>
            <div className="status-page-feature">
              {this.getIconForFeature('api')}&nbsp;Portfolio API
            </div>
            <div className="status-page-feature">
              {this.getIconForFeature('sk')}&nbsp;Smart Keywords
            </div>
            <div className="status-page-feature">
              <i className="fa fa-plus-circle fa-lg has-text-primary licenseIcon" />
              <a
                href={this.props.intl.formatMessage(
                  localizedStrings.EXTENSIS_CONTACT_US_URL
                )}
                rel="noopener noreferrer"
                target="_blank"
              >
                &nbsp;
                <FormattedMessage {...localizedStrings.LIC_CLICK_TO_UPGRADE} />
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

let wrappedForm = injectIntl(ProductDetails);

export default (wrappedForm = connect(state => ({
  ApiVersion: getApiVersion(state),
  assetCapCount: getAssetCapCount(state),
  enabledMediaEnginesCount: getEnabledMediaEnginesCount(state),
  extensisAccount: getExtensisAccount(state),
  licenses: getLicenses(state),
  licensedFeatures: getLicensedFeatures(state),
  maxSeatCount: getMaxSeatCount(state),
  mediaEngineTotalSeatCount: getMediaEngineTotalSeatCount(state),
  seatCount: getSeatCount(state),

  totalAssetCount: getTotalAssetCount(state),
}))(wrappedForm));



// WEBPACK FOOTER //
// ./src/view/Pages/StatusPage/ProductDetails.js